/* =============================================================================
   ÍNDICE DE CONTENIDO — el cerebro de la estructura SILO
   -----------------------------------------------------------------------------
   Este archivo no contiene texto. Lo que hace es leer las tres categorías y
   calcular, a partir de ellas:

     · Todas las URLs del sitio (ES y CA)
     · Qué páginas están publicadas y cuáles no
     · Las equivalencias entre idiomas, para el hreflang
     · Los datos de cada página para pintarla y para el <head>

   Lo usan tres cosas a la vez:
     1. El router de Vue (para crear las rutas)
     2. Los componentes PageCategoria y PageServicio (para el contenido)
     3. El script scripts/prerender.js (para generar el HTML estático)

   Está escrito en CommonJS a propósito: así el script de Node puede leerlo
   directamente sin necesidad de compilar nada.
   ============================================================================= */

var site = require('./site');
var SITE = site.SITE;
var IDIOMAS = site.IDIOMAS;

var vehiculos = require('./vehiculos');
var transporte = require('./transporte');
var seguros = require('./seguros');

/* El orden de este array es el orden en que aparecen en menús y sitemap */
var CATEGORIAS = [vehiculos, transporte, seguros];

var IDIOMAS_LISTA = ['es', 'ca'];

/* -----------------------------------------------------------------------------
   Construcción de URLs
   -------------------------------------------------------------------------- */

function urlCategoria(categoria, lang) {
  return IDIOMAS[lang].prefijo + '/' + categoria.slug[lang];
}

function urlServicio(categoria, servicio, lang) {
  return IDIOMAS[lang].prefijo + '/' + categoria.slug[lang] + '/' + servicio.slug[lang];
}

function urlHome(lang) {
  return IDIOMAS[lang].prefijo || '/';
}

/* -----------------------------------------------------------------------------
   Búsquedas
   -------------------------------------------------------------------------- */

function getCategoria(id) {
  for (var i = 0; i < CATEGORIAS.length; i++) {
    if (CATEGORIAS[i].id === id) return CATEGORIAS[i];
  }
  return null;
}

function getServicio(categoriaId, slug, lang) {
  var cat = getCategoria(categoriaId);
  if (!cat) return null;
  for (var i = 0; i < cat.servicios.length; i++) {
    if (cat.servicios[i].slug[lang] === slug) return cat.servicios[i];
  }
  return null;
}

/* Servicios publicados de una categoría, en el orden del archivo */
function serviciosPublicados(categoria) {
  return categoria.servicios.filter(function (s) {
    return s.publicada === true;
  });
}

/* Servicios publicados agrupados por el campo "grupo" */
function serviciosPorGrupo(categoria, lang) {
  var publicados = serviciosPublicados(categoria);
  var salida = [];

  (categoria.grupos || []).forEach(function (grupo) {
    var delGrupo = publicados.filter(function (s) {
      return s.grupo === grupo.id;
    });
    if (delGrupo.length) {
      salida.push({
        id: grupo.id,
        titulo: grupo[lang].titulo,
        subtitulo: grupo[lang].subtitulo,
        servicios: delGrupo
      });
    }
  });

  /* Servicios sin grupo declarado, para que nunca desaparezcan por descuido */
  var idsGrupos = (categoria.grupos || []).map(function (g) { return g.id; });
  var huerfanos = publicados.filter(function (s) {
    return idsGrupos.indexOf(s.grupo) === -1;
  });
  if (huerfanos.length) {
    salida.push({ id: 'otros', titulo: '', subtitulo: '', servicios: huerfanos });
  }

  return salida;
}

/* -----------------------------------------------------------------------------
   MAPA DE PÁGINAS
   Devuelve una lista plana de todo lo que existe en el sitio, con la
   información que necesitan el router, el sitemap y el prerender.
   -------------------------------------------------------------------------- */

function mapaPaginas() {
  var paginas = [];

  CATEGORIAS.forEach(function (cat) {
    /* Página de categoría (SILO) */
    IDIOMAS_LISTA.forEach(function (lang) {
      paginas.push({
        tipo: 'categoria',
        categoriaId: cat.id,
        slug: cat.slug[lang],
        lang: lang,
        url: urlCategoria(cat, lang),
        /* Equivalentes en el otro idioma, para el hreflang */
        alternativas: IDIOMAS_LISTA.reduce(function (acc, l) {
          acc[l] = urlCategoria(cat, l);
          return acc;
        }, {}),
        prioridad: 0.9
      });
    });

    /* Páginas comerciales publicadas */
    serviciosPublicados(cat).forEach(function (serv) {
      IDIOMAS_LISTA.forEach(function (lang) {
        paginas.push({
          tipo: 'servicio',
          categoriaId: cat.id,
          slug: serv.slug[lang],
          lang: lang,
          url: urlServicio(cat, serv, lang),
          alternativas: IDIOMAS_LISTA.reduce(function (acc, l) {
            acc[l] = urlServicio(cat, serv, l);
            return acc;
          }, {}),
          prioridad: 0.8
        });
      });
    });
  });

  return paginas;
}

/* -----------------------------------------------------------------------------
   DATOS DE UNA PÁGINA
   Todo lo que necesita el componente (o el prerender) para pintarla.
   -------------------------------------------------------------------------- */

function datosCategoria(categoriaId, lang) {
  var cat = getCategoria(categoriaId);
  if (!cat) return null;

  var c = cat.categoria[lang];

  return {
    tipo: 'categoria',
    lang: lang,
    categoria: cat,
    icono: cat.icono,
    whatsapp: cat.whatsappPropio ? SITE.whatsappSeguros : SITE.whatsapp,

    /* El silo de seguros tiene número propio (el de Alex) y cierra con el
       formulario de presupuesto, igual que la home. Ese número es solo de
       WhatsApp, así que en su bloque de CTA no se ofrece llamar. */
    telefono: cat.whatsappPropio ? SITE.telefonoSeguros : SITE.telefono,
    telefonoTexto: cat.whatsappPropio ? SITE.telefonoSegurosTexto : SITE.telefonoTexto,
    soloWhatsapp: !!cat.whatsappPropio,
    formularioSeguro: !!cat.whatsappPropio,

    titleSeo: c.titleSeo,
    description: c.description,
    h1: c.h1,
    entradilla: c.entradilla,
    secciones: c.secciones || [],
    faqs: c.faqs || [],
    ctaTitulo: c.ctaTitulo,
    ctaTexto: c.ctaTexto,
    ctaWhatsapp: c.ctaWhatsapp,

    /* Bloques opcionales. Solo los usa la categoría que los declare; las que
       no los tengan siguen pintándose exactamente igual que antes. */
    subtitulo: c.subtitulo || '',
    heroCta: c.heroCta || '',
    serviciosTitulo: c.serviciosTitulo || '',
    bloquesServicio: c.bloquesServicio || [],
    proceso: c.proceso || null,
    resenas: c.resenas || null,

    grupos: serviciosPorGrupo(cat, lang),

    url: urlCategoria(cat, lang),
    alternativas: IDIOMAS_LISTA.reduce(function (acc, l) {
      acc[l] = urlCategoria(cat, l);
      return acc;
    }, {}),

    migas: [
      { titulo: lang === 'ca' ? 'Inici' : 'Inicio', url: urlHome(lang) },
      { titulo: c.h1, url: urlCategoria(cat, lang) }
    ]
  };
}

function datosServicio(categoriaId, slug, lang) {
  var cat = getCategoria(categoriaId);
  if (!cat) return null;

  var serv = getServicio(categoriaId, slug, lang);
  if (!serv || serv.publicada !== true) return null;

  var s = serv[lang];

  /* Los "relacionados" se guardan siempre con el slug en castellano,
     así no hay que mantener dos listas. Aquí los traducimos al idioma
     que toque y descartamos los que no estén publicados. */
  var relacionados = (s.relacionados || [])
    .map(function (slugEs) {
      var encontrado = getServicio(categoriaId, slugEs, 'es');
      if (!encontrado) {
        /* Puede apuntar a otra categoría */
        for (var i = 0; i < CATEGORIAS.length; i++) {
          var otro = getServicio(CATEGORIAS[i].id, slugEs, 'es');
          if (otro) return { cat: CATEGORIAS[i], serv: otro };
        }
        return null;
      }
      return { cat: cat, serv: encontrado };
    })
    .filter(function (r) {
      return r && r.serv.publicada === true;
    })
    .map(function (r) {
      return {
        titulo: r.serv[lang].h1,
        resumen: r.serv[lang].resumen,
        icono: r.serv.icono,
        url: urlServicio(r.cat, r.serv, lang)
      };
    });

  return {
    tipo: 'servicio',
    lang: lang,
    categoria: cat,
    servicio: serv,
    icono: serv.icono,
    whatsapp: cat.whatsappPropio ? SITE.whatsappSeguros : SITE.whatsapp,

    telefono: cat.whatsappPropio ? SITE.telefonoSeguros : SITE.telefono,
    telefonoTexto: cat.whatsappPropio ? SITE.telefonoSegurosTexto : SITE.telefonoTexto,
    soloWhatsapp: !!cat.whatsappPropio,
    formularioSeguro: !!cat.whatsappPropio,

    titleSeo: s.titleSeo,
    description: s.description,
    h1: s.h1,
    kwPrincipal: s.kwPrincipal,
    entradilla: s.entradilla,
    secciones: s.secciones || [],
    faqs: s.faqs || [],
    ctaTitulo: s.ctaTitulo,
    ctaTexto: s.ctaTexto,
    ctaWhatsapp: s.ctaWhatsapp,
    relacionados: relacionados,

    url: urlServicio(cat, serv, lang),
    alternativas: IDIOMAS_LISTA.reduce(function (acc, l) {
      acc[l] = urlServicio(cat, serv, l);
      return acc;
    }, {}),

    migas: [
      { titulo: lang === 'ca' ? 'Inici' : 'Inicio', url: urlHome(lang) },
      { titulo: cat.categoria[lang].h1, url: urlCategoria(cat, lang) },
      { titulo: s.h1, url: urlServicio(cat, serv, lang) }
    ]
  };
}

/* -----------------------------------------------------------------------------
   NAVEGACIÓN
   Las tres categorías, para menús y footer.
   -------------------------------------------------------------------------- */
function navegacionSilos(lang) {
  return CATEGORIAS.map(function (cat) {
    return {
      id: cat.id,
      /* Etiqueta corta para el menú; si la categoría no la declara, el h1 */
      titulo: (cat.tituloMenu && cat.tituloMenu[lang]) || cat.categoria[lang].h1,
      tituloLargo: cat.categoria[lang].h1,
      icono: cat.icono,
      url: urlCategoria(cat, lang)
    };
  });
}

module.exports = {
  SITE: SITE,
  IDIOMAS: IDIOMAS,
  IDIOMAS_LISTA: IDIOMAS_LISTA,
  CATEGORIAS: CATEGORIAS,
  getCategoria: getCategoria,
  getServicio: getServicio,
  serviciosPublicados: serviciosPublicados,
  serviciosPorGrupo: serviciosPorGrupo,
  mapaPaginas: mapaPaginas,
  datosCategoria: datosCategoria,
  datosServicio: datosServicio,
  navegacionSilos: navegacionSilos,
  urlCategoria: urlCategoria,
  urlServicio: urlServicio,
  urlHome: urlHome
};
